
import hasStickyFilter from '@/mixins/hasStickyFilter';
import hasStickyData from '@/mixins/hasStickyData';
import hasCustomFields from '../../mixins/hasCustomFields';
import { hideAllPoppers } from 'floating-vue';
import { mapState } from 'vuex';

export default {
  name: 'FiltersBar',
  mixins: [hasStickyFilter, hasCustomFields, hasStickyData],

  props: {
    filterKey: {
      type: String,
      required: true,
    },
    pageFilters: {
      type: Object,
      required: true,
    },
    customFieldModel: {
      type: String,
      required: false,
    },
    showDisplayOptions: {
      type: Boolean,
      required: false,
      default: true,
    },
    loadSaved: {
      type: Boolean,
      required: false,
      default: true,
    },
    refreshOnDisplayChange: {
      type: Boolean,
      required: false,
      default: false,
    },
    additionalData: {
      type: Object,
      required: false,
    },
    folder: {
      type: Object,
      required: false,
    },
    additionalFilters: {
      type: Array,
      required: false,
    },
    display: {
      type: String,
      default: 'auto', // "auto" | "compact"
    },
    stickyFilters: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      search: '',
      selected: [],
      filters: {
        per_page: this.pageFilters?.per_page || 10,
        sort: this.pageFilters?.sort?.default || null,
      },
      allAvailableFilters: [],
      availableFilters: [],
      loaded: false,
      displayType: 'list',
      perPageOptions: [10, 20, 30, 50, 100, 250],
      perPageShown: false,
      addedFilters: {},
    };
  },
  async created() {
    if (!this.loadSaved) {
      this.selected = [];
    }
    this.allAvailableFilters = this.pageFilters.filters;
    await this.buildAvailableFilters();

    // set default values
    this.getDefaultValues();
    this.buildFiltersForPageLoad();
    this.closeAllFilters();

    if (this.stickyFilters) {
      const hasFilters = await this.getExistingData();
      if (!hasFilters) {
        await this.syncAddedFiltersWithDefaults();
      } else {
        await this.syncAddedFiltersWithDefaults();
      }
    } else {
      await this.syncAddedFiltersWithDefaults();
    }

    // Set loaded to true and apply filters once
    this.loaded = true;
    this.applyFilters(this.filters);
  },

  methods: {
    closeAllFilters() {
      // allAvailableFilters may not be populated yet in some lifecycle hooks, guard it
      if (Array.isArray(this.allAvailableFilters)) {
        this.allAvailableFilters.forEach((f) => {
          f.open = false;
        });
      }
      if (Array.isArray(this.selected)) {
        this.selected.forEach((f) => {
          f.open = false;
        });
      }
      if (Array.isArray(this.availableFilters)) {
        this.availableFilters.forEach((f) => {
          f.open = false;
        });
      }
    },
    buildFiltersForPageLoad() {
      let currentFilters = this.pageFilters.filters.filter((filter) => {
        return filter?.showOnPageLoad === true;
      });

      currentFilters.forEach((filter) => {
        if (!this.selected.includes(filter)) {
          this.selected.push(filter);
        }
      });
    },

    getDefaultValues() {
      this.pageFilters.filters.forEach((filter) => {
        if ('getDefaultValue' in filter && typeof filter.getDefaultValue === 'function') {
          const defaultValue = filter.getDefaultValue(this);
          if (defaultValue !== undefined && defaultValue !== null) {
            this.$set(this.filters, filter.field, defaultValue);
          }
        }
      });
    },

    async addFilter(filter) {
      if (!this.selected.includes(filter)) {
        filter.open = true;
        this.selected.unshift(filter);
      }

      await this.buildAvailableFilters();
      this.hideAll();
    },

    hideAll() {
      hideAllPoppers();
    },
    async refresh() {
      const hasFilters = await this.getExistingData();
      if (!hasFilters) {
        this.applyFilters(this.filters);
      }
    },
    setPerPage(amount) {
      this.filters.per_page = amount;
      this.perPageShown = false;
    },
    async removeFilter(filter) {
      await this.$delete(this.filters, filter.field);

      this.selected = this.selected.filter((item) => {
        return item !== filter;
      });

      if (this.stickyFilters) {
        await this.storeFilters(this.filters);
      }

      // Remove any filters that are no longer visible after the original filter is removed
      const stillVisible = [];
      // this is a problem when a default additional filter is allocated, if additionalFilters includes it don't remove the filter
      for (const item of this.selected) {
        if (
          typeof item.isVisible === 'function' &&
          !item.isVisible(this) &&
          this.additionalFilters?.some((filter) => filter.field === item.field) !== true
        ) {
          this.$delete(this.filters, item.field);
        } else {
          stillVisible.push(item);
        }
      }
      this.selected = stillVisible;
      await this.storeFilters(this.filters);
      await this.buildAvailableFilters();
    },
    async getExistingData() {
      // get the view options
      let hasFilters = false;
      let display = await this.getStickyData(this.filterKey + '-viewOptions');
      if (display === null) {
        this.displayType = 'grid';
      } else {
        this.displayType = display;
      }

      this.$emit('displayChanged', this.displayType);
      // if route includes contact, plan, case, task
      const paths = ['plans/', 'contacts/', 'cases/', 'tasks/', 'circle/'];
      if (paths.some((sub) => this.$route.path.includes(sub))) {
        this.stickyFilterKey = this.filterKey + '-' + this.$route.params.id;
      } else {
        this.stickyFilterKey = this.filterKey;
      }
      if (this.stickyFilterKey) {
        let filter = await this.getStickyFilters();
        this.loaded = true;
        if (filter) {
          // Merge sticky filters with existing defaults instead of replacing
          this.filters = { ...this.filters, ...filter };

          // if the per_page is not a valid value, set it to the default
          if (this.filters?.per_page && !this.perPageOptions.includes(this.filters.per_page)) {
            this.filters.per_page = this.pageFilters?.per_page || 10;
          }

          let keys = Object.keys(this.filters);
          this.selected = this.allAvailableFilters.filter((item) => {
            return keys.includes(item.field);
          });
          // close all the sticky filters on load
          this.selected.forEach((filter) => {
            filter.open = false;
          });
          this.closeAllFilters();
          hasFilters = true;
        }
        await this.syncAddedFiltersWithDefaults();
        return hasFilters;
      }
    },
    async storeFilters(filters) {
      let f = this.$nuxt.$helpers.removeNullPropertiesInObject(filters);
      this.filters = f;
      await this.setStickyFilters(f);
    },
    applyFilters(filters) {
      // run the filterDatafileBeforeApplyFilters method
      if (this.pageFilters.methods.beforeApplyFilters !== undefined) {
        this.selected = this.pageFilters?.methods.beforeApplyFilters(
          this.selected,
          this.allAvailableFilters,
          this.filters
        );
      }

      // this checks if the current pageFilters has the API call enabled, or not. The default here is true, so if hasAPICall isn't set on the json file, that's fine.
      const hasApiCall = this.pageFilters?.hasAPICall ?? true;

      if (hasApiCall) {
        // runs if the current page filters have API calls
        let call = this.pageFilters.methods.applyFilters(filters, this, this.additionalData);
        call
          .then((response) => {
            if (this.pageFilters.methods.afterFilterChange !== undefined) {
              this.pageFilters.methods.afterFilterChange(filters, this.additionalData);
            }
            this.$emit('gotResults', response?.data || response);
          })
          .catch((error) => {
            this.$nuxt.$emit('notification', 'Oops, something went wrong, please refresh and try again.', 'warning');
          });
      } else {
        // run if the current pageFilters DOES NOT have an API call to make, and instead emits an event
        if (this.pageFilters.methods.afterFilterChange !== undefined) {
          this.pageFilters.methods.afterFilterChange(filters, this, this.additionalData);
        }
        this.$nuxt.$emit(this.pageFilters.emitName, this.pageFilters.emitParams);
      }
    },
    setFocus() {
      setTimeout(() => {
        const item = document.getElementById('filterSearch');
        item.focus();
      }, 20);
    },

    async buildAvailableFilters() {
      let options = [];
      if (this.search.length === 0) {
        options = this.dynamicOptions.filter((item) => {
          if (item.isVisible !== undefined) {
            return item.isVisible(this);
          } else {
            return true;
          }
        });

        options = options.filter((item) => {
          return !this.selected.includes(item);
        });

        this.availableFilters = options.sort((a, b) => (a.name < b.name ? -1 : 1));
      } else {
        options = this.dynamicOptions.filter((item) => {
          return !this.selected.includes(item) && item.name.toLowerCase().includes(this.search.toLowerCase());
        });

        options = options.filter((item) => {
          if (item.isVisible !== undefined) {
            return item.isVisible(this);
          } else {
            return true;
          }
        });

        this.availableFilters = options.sort((a, b) => (a.name < b.name ? -1 : 1));
      }
    },

    async runShortcut(shortcut) {
      this.filters = shortcut.handler(this.filters, this);
      if (this.stickyFilters) {
        await this.storeFilters(this.filters);
      }
    },

    changeDisplayType(val) {
      this.displayType = val;
      this.setStickyData(this.filterKey + '-viewOptions', val);
      this.$emit('displayChanged', val);

      if (this.refreshOnDisplayChange) {
        this.applyFilters(this.filters);
      }
    },

    async resetFilters() {
      this.$nuxt.$emit('resetAdditionalFilters');
      this.addedFilters = {};
      this.selected = [];
      this.filters = {
        per_page: this.pageFilters?.per_page || 10,
        sort: this.pageFilters?.sort?.default || null,
      };
      let fields = this.buildForFilters(this.customFields);
      this.allAvailableFilters = [...this.pageFilters.filters, ...fields];
      this.getDefaultValues();
      this.syncAddedFiltersWithDefaults();
      this.buildFiltersForPageLoad();
      await this.buildAvailableFilters();
    },
    reapplyFilters() {
      this.applyFilters(this.filters);
    },
    onFilterChanged(filter, value, oldValue) {
      if ('onFilterChanged' in filter && typeof filter.onFilterChanged === 'function') {
        filter.onFilterChanged(value, this);
      }
    },
    async syncAddedFiltersWithDefaults() {
      if (!this.additionalFilters || this.additionalFilters.length === 0) return;

      this.additionalFilters.forEach((addFilter) => {
        const field = addFilter.field;
        if (this.filters.hasOwnProperty(field) && this.filters[field] !== undefined && this.filters[field] !== null) {
          // Make sure to sync the default or sticky filter value
          this.$set(this.addedFilters, field, this.filters[field]);
        }
      });
    },
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapState('client', ['client']),
    ...mapState('circle', ['circle']),
    ...mapState('localStorage', ['folderView']),

    gridColumns() {
      if (this.additionalFilters && this.additionalFilters.length === 1) {
        return 'grid-cols-1';
      }
      return 'grid-cols-1 md:grid-cols-2';
    },
    selectedAndVisible() {
      return this.selected.filter((item) => {
        if (item.isVisible !== undefined) {
          return item.isVisible(this);
        } else {
          return true;
        }
      });
    },

    showPerPage() {
      if (this.pageFilters?.gridType === 'kanban' && this.displayType === 'grid') {
        return false;
      }

      return true;
    },
    sort() {
      return this.pageFilters?.sort;
    },

    filtersApplied() {
      return Object.keys(this.filters).some((key) => key.startsWith('filter['));
    },

    dynamicOptions() {
      let options = this.allAvailableFilters;
      options.forEach((item) => {
        if (item.setOptions !== undefined) {
          item.options = item.setOptions(this);
        }
      });
      return options;
    },
  },
  watch: {
    search: {
      deep: true,
      async handler(newVal) {
        await this.buildAvailableFilters();
      },
    },
    selected: {
      deep: true,
      async handler(val) {
        await this.buildAvailableFilters();
      },
    },
    filterKey: {
      handler(val) {
        if (val && this.loadSaved) {
          this.getExistingData();
        }
      },
    },

    '$route.params.id': {
      handler(newId, oldId) {
        if (newId !== oldId && this.stickyFilters && this.loadSaved) {
          this.getExistingData();
        }
      },
    },
    customFieldModel: {
      immediate: true,
      handler(val) {
        if (val) {
          this.modelType = val;
        }
      },
    },
    addedFilters: {
      deep: true,
      handler(val) {
        // merge into filters block
        this.$set(this, 'filters', { ...this.filters, ...this.addedFilters });
        this.applyFilters(this.filters);
      },
    },
    filters: {
      deep: true,
      async handler(val, oldVal) {
        if (val && this.loaded) {
          await this.setStickyFilters(val);
        }

        if (this.additionalFilters) {
          const hasSearchInput = this.additionalFilters.filter((item) => item.component === 'ui-search-input');
          if (hasSearchInput.length > 0) {
            Object.keys(val).forEach((key) => {
              if (val[key] == null || (val[key] === '' && typeof val[key] !== 'boolean')) {
                delete val[key];
              }
            });
          }
        }

        // Only apply filters after component is fully loaded to prevent duplicate calls during setup
        if (this.loaded) {
          this.$emit('applyingFilters');
          this.applyFilters(val);
          await this.buildAvailableFilters();
        }
      },
    },
    folder: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal && oldVal && newVal.uuid !== oldVal.uuid) {
          this.applyFilters(this.filters);
        }
      },
    },

    customFields: {
      deep: true,
      async handler(val, oldVal) {
        // kick off the process of building the list of custom fields as per our hard-coded version
        if (val !== oldVal) {
          let fields = this.buildForFilters(this.filters);
          this.allAvailableFilters = [...this.allAvailableFilters, ...fields];
          await this.buildAvailableFilters();
        }
      },
    },
  },
};
